import { Link, useParams, useSearch } from '@tanstack/react-router';
import {
  Alert,
  Card,
  EmptyState,
  Page,
  PageHeader,
  StatCard,
  StatCardList,
  Table,
  backLinkClasses,
} from '@evogard/ui';
import {
  format as formatMoney,
  formatDate,
  sum,
  type BillingAwardDay,
  type BillingAwardWeekResult,
} from '@evogard/domain';
import {
  currentPeriod,
  mergeAwards,
  periodLabel,
  useAwardPeriod,
} from './useAwards.js';

/**
 * Um operador da premiação na competência — a tela `/dashboard/operador/[id]`
 * da `PremiacaoCobrancaRetencao` (23/10/2026).
 *
 * Os mesmos blocos de lá: o recuperado e a bonificação de cada meta, os dias em
 * que bateu a meta diária, as semanas em que bateu a semanal e os boletos por
 * tipo. A conta é a do painel (`mergeAwards` → `billingAwardsOf`): CE e GO
 * somadas antes da faixa, e nada calculado só aqui — duas contas seriam a tela
 * do operador discordando do relatório de pagamento.
 */
export function AwardsOperatorScreen() {
  const { operatorId } = useParams({ from: '/authenticated/billing/awards/operator/$operatorId' });
  const search = useSearch({ from: '/authenticated/billing/awards/operator/$operatorId' });
  const period = search.period ?? currentPeriod();
  const states = useAwardPeriod(period);
  const merged = mergeAwards(states);
  const loading = states.some((b) => b.state === 'carregando');

  const back = (
    <Link to="/billing/awards" className={backLinkClasses()}>
      Premiação da cobrança
    </Link>
  );

  if (merged.state !== 'calculado') {
    return (
      <Page measure="cheia">
        <PageHeader title="Operador da premiação" back={back} />
        {loading ? (
          <p className="text-sm text-texto-suave">Carregando a competência…</p>
        ) : merged.state === 'recusado' ? (
          <Alert tone="alerta">{merged.problem}</Alert>
        ) : (
          <EmptyState icon="grafico" title="Sem premiação nesta competência">
            Nenhuma das suas bases respondeu com operadores ativos na premiação.
          </EmptyState>
        )}
      </Page>
    );
  }

  const line = merged.lines.find((l) => l.id === operatorId);
  const result = merged.result.operators.find((o) => o.operatorId === operatorId);
  if (!line || !result) {
    return (
      <Page measure="cheia">
        <PageHeader title="Operador da premiação" back={back} />
        <Alert tone="informacao">
          Este operador não está ativo na premiação de {periodLabel(period)} em nenhuma das suas bases.
        </Alert>
      </Page>
    );
  }

  const enabled = merged.rule.enabled;
  const days = result.days.filter((d) => d.tier !== null);
  const weeks = result.weeks.filter((w) => w.tier !== null);

  return (
    <Page measure="cheia">
      <PageHeader title={line.name} back={back} />
      <p className="text-sm text-texto-suave">
        {periodLabel(period)} · {merged.frozen ? 'valor congelado do sistema antigo' : `${line.records} atendimento(s)`} ·{' '}
        {result.paidCount} boleto(s) pago(s) com vínculo
        {merged.closed ? '' : ' · competência aberta: o resultado é prévia'}
      </p>

      <StatCardList>
        <StatCard icon="dinheiro" label="Recuperado" value={formatMoney(result.recovered)} support="as bases somadas" />
        {enabled.diaria && (
          <StatCard
            icon="calendario"
            label="Bonificação diária"
            value={formatMoney(result.dailyBonus)}
            support={`${days.length} dia(s) com a meta batida`}
          />
        )}
        {enabled.semanal && (
          <StatCard
            icon="calendario"
            label="Bonificação semanal"
            value={formatMoney(result.weeklyBonus)}
            support={`${weeks.length} semana(s) com a meta batida`}
          />
        )}
        {enabled.mensal && (
          <StatCard
            icon="grafico"
            label="Faixa mensal"
            value={result.monthlyTier ? result.monthlyTier.label : '—'}
            support={result.monthlyTier ? formatMoney(result.monthlyBonus) : 'ainda não alcançou a primeira faixa'}
          />
        )}
        <StatCard
          icon="percentual"
          label="Bonificação total"
          value={formatMoney(result.total)}
          support="diária, semanal e mensal"
        />
      </StatCardList>

      {enabled.diaria && (
        <Card
          title="Dias em que bateu a meta diária"
          descricao="Pelo dia do pagamento. Só aparecem os dias que alcançaram alguma faixa."
          bleed
        >
          <Table<BillingAwardDay>
            label="Dias com a meta diária batida"
            empty="Nenhum dia alcançou a primeira faixa."
            items={days}
            rowKey={(d) => d.date}
            paginate={false}
            columns={[
              { title: 'Data', alignment: 'inicio', cell: (d) => formatDate(d.date) },
              { title: 'Dia da semana', alignment: 'inicio', cell: (d) => weekdayOf(d.date) },
              { title: 'Recuperado', alignment: 'fim', cell: (d) => formatMoney(d.recovered) },
              { title: 'Faixa', alignment: 'inicio', cell: (d) => d.tier?.label ?? '—' },
              { title: 'Bonificação', alignment: 'fim', cell: (d) => <strong>{formatMoney(d.bonus)}</strong> },
            ]}
          />
        </Card>
      )}

      {enabled.semanal && (
        <Card
          title="Semanas em que bateu a meta semanal"
          descricao="De segunda a domingo, pelo dia do pagamento; a semana é do mês que tem o domingo, e pode somar o fim do mês anterior."
          bleed
        >
          <Table<BillingAwardWeekResult>
            label="Semanas com a meta semanal batida"
            empty="Nenhuma semana alcançou a primeira faixa."
            items={weeks}
            rowKey={(w) => w.start}
            paginate={false}
            columns={[
              { title: 'Semana', alignment: 'inicio', cell: (w) => `${formatDate(w.start)} a ${formatDate(w.end)}` },
              { title: 'Recuperado', alignment: 'fim', cell: (w) => formatMoney(w.recovered) },
              { title: 'Faixa', alignment: 'inicio', cell: (w) => w.tier?.label ?? '—' },
              { title: 'Bonificação', alignment: 'fim', cell: (w) => <strong>{formatMoney(w.bonus)}</strong> },
            ]}
          />
        </Card>
      )}

      <Card title="Boletos por tipo" descricao="Os boletos pagos com vínculo que conta, pelo tipo do boleto." bleed>
        <Table<(typeof result.byType)[number]>
          label="Boletos pagos por tipo"
          empty="Nenhum boleto pago com vínculo nesta competência."
          items={[...result.byType]}
          rowKey={(t) => t.typeLabel}
          paginate={false}
          columns={[
            { title: 'Tipo', alignment: 'inicio', cell: (t) => t.typeLabel },
            { title: 'Quantidade', alignment: 'fim', cell: (t) => t.count },
            { title: 'Recuperado', alignment: 'fim', cell: (t) => formatMoney(t.recovered) },
          ]}
        />
        <p className="px-4 py-3 text-sm text-texto-suave">
          Total: {formatMoney(sum(...result.byType.map((t) => t.recovered)))}
        </p>
      </Card>
    </Page>
  );
}

/** "segunda-feira" — o dia da semana de uma data de negócio, sem fuso no caminho. */
export function weekdayOf(date: string): string {
  return new Date(`${date}T12:00:00Z`).toLocaleDateString('pt-BR', { weekday: 'long', timeZone: 'UTC' });
}
